import React, { useMemo } from 'react';
import { SupplierJobUpdate } from '@dill/dill-shared';

type VerifiedJobDataPick = Pick<
  SupplierJobUpdate,
  | 'owners'
  | 'GCs'
  | 'lenders'
  | 'apn'
  | 'constructionMonitorPermitId'
  | 'estimatedMaterialAmount'
  | 'jobType'
  | 'jobSector'
  | 'customerType'
  | 'jobFurnishings'
  | 'hasNoLender'
  | 'jobAddress'
  | 'jobCity'
  | 'jobState'
  | 'jobZipCode'
>;

const VerifySupplierJob = ({
  verifyJobData,
  ownerVerificationEnable,
}: {
  verifyJobData: VerifiedJobDataPick;
  ownerVerificationEnable?: boolean;
}) => {
  const checkIfNotDataFound = useMemo(() => {
    return (
      verifyJobData.owners?.filter((item) => item.name === '').length !== 0 &&
      verifyJobData.GCs?.filter((item) => item.name === '').length !== 0 &&
      verifyJobData?.apn === ''
    );
  }, [verifyJobData]);

  return (
    <div>
      <div className="p-6">
        <div className="text-xs my-2 font-semibold">For the job address you entered:</div>
        <div className="my-2 font-semibold PRIMARY_500-CLR">{`${verifyJobData.jobAddress ?? ''}, ${
          verifyJobData.jobCity ?? ''
        }, ${verifyJobData.jobState ?? ''} ${verifyJobData.jobZipCode ?? ''}`}</div>
        {!checkIfNotDataFound && (
          <div className="text-xs my-2 font-semibold">Dill automatically identified:</div>
        )}
        {checkIfNotDataFound && ownerVerificationEnable && (
          <div className="flex flex-col h-full  w-full border rounded-lg overflow-hidden p-3 items-center">
            <div className="text-sm my-2 font-semibold text-red-500">
              No matching property found
            </div>
          </div>
        )}
        {!checkIfNotDataFound && (
          <div className="flex flex-col w-full border rounded-lg p-2 max-h-[calc(90vh-260px)] overflow-y-auto">
            {verifyJobData.jobAddress && (
              <div className="flex items-center">
                <div className="text-sm my-2 font-semibold GREY_500-CLR w-[140px] pr-2">
                  Job Address
                </div>
                <div className="text-sm my-2 font-semibold">{`${verifyJobData.jobAddress ?? ''}, ${
                  verifyJobData.jobCity ?? ''
                }, ${verifyJobData.jobState ?? ''} ${verifyJobData.jobZipCode ?? ''}`}</div>
              </div>
            )}
            {verifyJobData.apn && (
              <div className="flex items-center">
                <div className="text-sm my-2 font-semibold GREY_500-CLR w-[140px] pr-2">
                  Property APN
                </div>
                <div className="text-sm my-2 font-semibold">{verifyJobData.apn}</div>
              </div>
            )}
            {verifyJobData.owners &&
              verifyJobData.owners.filter((owner) => owner.name !== '').length > 0 &&
              verifyJobData.owners
                .filter((owner) => owner.name !== '')
                .map((owner) => (
                  <div key={`verify_owner_job_${owner.name}`}>
                    {owner.name && (
                      <div className="flex items-center">
                        <div className="text-sm my-2 font-semibold GREY_500-CLR w-[140px] pr-2">
                          Owner Name
                        </div>
                        <div className="text-sm my-2 font-semibold">{owner.name}</div>
                      </div>
                    )}
                    {owner.address && (
                      <div className="flex items-center">
                        <div className="text-sm my-2 font-semibold GREY_500-CLR w-[140px] pr-2">
                          Owner Address, City, State, Zip
                        </div>
                        <div className="text-sm my-2 font-semibold">{`${owner.address ?? ''}, ${
                          owner.city ?? ''
                        }, ${owner.state ?? ''} ${owner.zipCode ?? ''}`}</div>
                      </div>
                    )}
                  </div>
                ))}
            {verifyJobData.GCs &&
              verifyJobData.GCs.filter((gc) => gc.name !== '').length > 0 &&
              verifyJobData.GCs.filter((gc) => gc.name !== '').map((gc) => (
                <div key={`verify_gc_job_${gc.name}`}>
                  {gc.name && (
                    <div className="flex items-center">
                      <div className="text-sm my-2 font-semibold GREY_500-CLR w-[140px] pr-2">
                        GC Name
                      </div>
                      <div className="text-sm my-2 font-semibold">{gc.name}</div>
                    </div>
                  )}
                  {gc.address && (
                    <div className="flex items-center">
                      <div className="text-sm my-2 font-semibold GREY_500-CLR w-[140px] pr-2">
                        GC Address, City, State, Zip
                      </div>
                      <div className="text-sm my-2 font-semibold">{`${gc.address ?? ''}, ${
                        gc.city ?? ''
                      }, ${gc.state ?? ''} ${gc.zipCode ?? ''}`}</div>
                    </div>
                  )}
                </div>
              ))}
            {verifyJobData.jobType && (
              <div className="flex items-center">
                <div className="text-sm my-2 font-semibold GREY_500-CLR w-[140px] pr-2 pr-2">
                  Job Type
                </div>
                <div className="text-sm my-2 font-semibold capitalize">{verifyJobData.jobType}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifySupplierJob;
